import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { blobToBase64 } from "../../features/shared/utils"
import { getProviderPhotoThumbnail } from "./loader"
import { RootState } from "../../reduxStore/store"
import {
  Practice,
  PracticeAffiliate,
  PracticeServiceInviteStatusDisplayId,
} from "../Practice/schema"
import { apiService } from "../../features/shared/apiService"
import { DataStatus } from "../enums"
import { ModalComponentType } from "./modalComponentRegistry"
import { PublicPractice, PublicProfilePractice } from "./schema"
import { FileStatusDisplayIdEnum } from "../Files/schema"
import { isEmpty } from "lodash"
import { PracticeRoleEnum } from "../Account/userSlice"
import { xUUID } from "../../routes"
import { Provider, ProviderStatusDisplayIdEnum } from "../Provider/schema"
import { PracticeTypeDisplayId } from "../enums/displayId.enum"
import { ApiError } from "../../features/shared/ApiError"
import appInsightsLogger from "../../features/logging/appInsightsLogger"
import {
  getBase64ImageFromStorage,
  saveBase64ImageWithEviction,
} from "../../features/shared/localStorageBase64"

type ShowProfileSidebarType = {
  practiceId?: string
  provider?: {
    id: string
    userId: string
    // Hydration object from cached search index instead of re-fetching from DB-backed endpoints.
    prefetched?: ProcessedProviderSearchResults
  }
  scrollToSection?: string
}

export type ShowProfileModalType =
  | {
      modalComponentRegistryKey: ModalComponentType
      panelStyleOverrides?: string
      titleStyleOverrides?: string
    }
  | undefined

type State = {
  showProfileModal?: ShowProfileModalType
  showProfileSidebar: ShowProfileSidebarType
  // TODO: type this
  loading: boolean
}

const initialState: State = {
  showProfileModal: undefined,
  showProfileSidebar: {
    practiceId: undefined,
    provider: undefined,
    scrollToSection: "",
  },
  loading: false,
}

export interface PracticeProfileData extends Practice {
  photoFileId?: string
}

export const profileSlice = createSlice({
  name: "profile",
  initialState: initialState,
  reducers: {
    setShowProfileModal: (
      state: State,
      { payload }: PayloadAction<ShowProfileModalType | undefined>,
    ) => {
      state.showProfileModal = payload
    },
    setShowProfileSidebar: (
      state: State,
      { payload }: PayloadAction<ShowProfileSidebarType>,
    ) => {
      state.showProfileSidebar = payload
    },
    setLoading: (state: State, { payload }: PayloadAction<boolean>) => {
      state.loading = payload
    },
    resetScrollToSection: (state: State) => {
      state.showProfileSidebar.scrollToSection = ""
    },
  },
})

export default profileSlice.reducer

// Export actions and reducer
export const {
  setShowProfileModal,
  setShowProfileSidebar,
  setLoading,
  resetScrollToSection,
} = profileSlice.actions

export const selectProfile = (state: RootState) => {
  return state.profile
}

export const fetchLocationFullPhoto = async (
  practiceId: string,
  photoId: string,
  setDataStatusCallBack?: (dataStatus: DataStatus) => void,
): Promise<string | undefined> => {
  let blobURL
  try {
    setDataStatusCallBack?.(DataStatus.SENDING)
    const blobPhoto =
      await apiService.practice.get.PRACTICE_LOCATION_FULL_PHOTO({
        photoId,
        practiceId,
      })
    blobURL = URL.createObjectURL(blobPhoto)
    setDataStatusCallBack?.(DataStatus.LOADED)
  } catch (error) {
    console.error(error)
    setDataStatusCallBack?.(DataStatus.ERROR)
  }
  return blobURL
}

export const fetchLocationPhotoThumbnail = async (
  practiceId: string,
  photoId: string,
  setDataStatusCallBack?: (dataStatus: DataStatus) => void,
): Promise<string | undefined> => {
  let blobURL
  try {
    setDataStatusCallBack?.(DataStatus.SENDING)
    const blobPhoto =
      await apiService.practice.get.PRACTICE_LOCATION_PHOTO_THUMBNAIL({
        photoId,
        practiceId,
      })
    blobURL = URL.createObjectURL(blobPhoto)
    setDataStatusCallBack?.(DataStatus.LOADED)
  } catch (error) {
    console.error(error)
    setDataStatusCallBack?.(DataStatus.ERROR)
  }
  return blobURL
}

const tracePublicProviderThumbnailHydrationFailure = (
  providerId: string,
  error: unknown,
) => {
  const status = (error as { response?: { status?: number } })?.response?.status
  const statusText = status ? ` status ${status}` : ""
  appInsightsLogger.traceError(
    `Public provider photo thumbnail hydration failed for provider ${providerId}${statusText}. Continuing without a renderable provider photo.`,
  )
}

const setProviderPhotoRenderable = (
  providerData: Provider,
  base64String: string,
) => {
  providerData.photoFileStatusDisplayId = FileStatusDisplayIdEnum.AVAILABLE
  providerData.providerPhoto = providerData.providerPhoto
    ? {
        ...providerData.providerPhoto,
        thumbnailBase64: base64String,
      }
    : undefined
}

const hydratePublicProviderPhotoThumbnail = async (providerData: Provider) => {
  if (!providerData.photoFileId) {
    providerData.photoFileStatusDisplayId = undefined
    providerData.providerPhoto = undefined
    return
  }

  if (
    providerData.photoFileStatusDisplayId ===
    FileStatusDisplayIdEnum.QUARANTINED
  ) {
    providerData.providerPhoto = undefined
    return
  }

  try {
    const cachedBase64 = getBase64ImageFromStorage(providerData.photoFileId)
    if (cachedBase64) {
      setProviderPhotoRenderable(providerData, cachedBase64)
      return
    }

    const photoResponse = await getProviderPhotoThumbnail(providerData.id!)
    if (photoResponse?.status === 202) {
      providerData.photoFileStatusDisplayId = FileStatusDisplayIdEnum.PROCESSING
      providerData.providerPhoto = undefined
      return
    }

    if (photoResponse?.status !== 200) {
      throw new Error(
        `Public provider photo thumbnail returned status ${photoResponse?.status}`,
      )
    }

    const fileBlob: Blob = photoResponse.data
    const base64String = await blobToBase64(fileBlob)
    saveBase64ImageWithEviction(providerData.photoFileId, base64String)
    setProviderPhotoRenderable(providerData, base64String)
  } catch (error) {
    tracePublicProviderThumbnailHydrationFailure(providerData.id!, error)
    providerData.photoFileStatusDisplayId = undefined
    providerData.providerPhoto = undefined
  }
}

export const fetchPublicProviderProfileData = async (
  provider: { id?: string; userId?: string },
  setDataStatusCallBack?: (dataStatus: DataStatus) => void,
) => {
  try {
    setDataStatusCallBack?.(DataStatus.LOADING)
    const providerData = await fetchPublicProviderData(provider)
    if (providerData) {
      await hydratePublicProviderPhotoThumbnail(providerData)
      setDataStatusCallBack?.(DataStatus.LOADED)
      return providerData
    }
    throw new Error(`404 NOT FOUND: ${provider}`)
  } catch (error) {
    setDataStatusCallBack?.(DataStatus.ERROR)
    console.error(error)
  }
}

export const fetchPublicPracticesData = async (
  provider: { id?: string; userId?: string },
  setDataStatusCallBack?: (dataStatus: DataStatus) => void,
): Promise<PublicPractice[] | undefined> => {
  try {
    setDataStatusCallBack?.(DataStatus.LOADING)
    const publicPractices = !isEmpty(provider.userId)
      ? await apiService.practice.get.PUBLIC_PRACTICES_BY_PROVIDER_USER_ID({
          userId: provider.userId!,
        })
      : await apiService.practice.get.PUBLIC_PRACTICES_BY_PROVIDER_PROVIDER_ID({
          providerId: provider.id ?? xUUID,
        })
    setDataStatusCallBack?.(DataStatus.LOADED)
    return publicPractices
  } catch (error) {
    setDataStatusCallBack?.(DataStatus.ERROR)
    console.error(error)
  }
}

export const fetchPublicProviderData = async (provider: {
  id?: string
  userId?: string
}) => {
  let providerData: Provider | undefined
  // If we only have the userId and no providerId, we need to fetch by userId
  if (!!provider.id) {
    ;[providerData] = await apiService.provider.get.PUBLIC_PROVIDERS_BY_ID([
      provider.id,
    ])
  } else if (!!provider.userId) {
    // lets catch 404 here in case of a userId that has not been linked to the detached provider yet
    try {
      ;[providerData] = await apiService.provider.post.PROVIDERS_FROM_USER_IDS({
        userIds: [provider.userId!],
      })
    } catch (error) {
      // let the 404 pass through
      if ((error as ApiError).statusCode === 404) {
        return undefined
      } else {
        throw error
      }
    }
  }

  return providerData
}

export const fetchProviderData = async (provider: {
  id?: string
  userId?: string
}) => {
  return !isEmpty(provider.userId)
    ? await apiService.provider.get.PROVIDER_BY_USER_ID({
        userId: provider.userId!,
      })
    : await apiService.provider.get.PROVIDER_BY_ID({
        providerId: provider.id ?? xUUID,
      })
}

export const fetchPracticeAffiliatesData = async (
  affiliates: PracticeAffiliate[],
  setDataStatusCallBack?: (dataStatus: DataStatus) => void,
): Promise<Provider[]> => {
  setDataStatusCallBack?.(DataStatus.LOADING)
  const providerAffiliates = affiliates?.filter((affiliate) => {
    const hasProviderRole = affiliate.roleDisplayIds.includes(
      PracticeRoleEnum.PROVIDER,
    )
    const hasInvites =
      affiliate.practiceInvites?.length && affiliate.practiceInvites.length > 0
    if (!hasInvites) {
      // If there are no invites, we can assume this affiliate created the practice
      // and is a primary provider.
      // Note: The practice and provider are created simultaneously for this onboarding scenario, so the provider is ensured to exist.
      return hasProviderRole
    }

    // Provider affiliates that are directly attached to the practice without an invite-
    // acceptance flow (v2 unregistered / migrated providers on standard practices, plus
    // managed providers on managed practices): include them. In v2 every provider
    // affiliate has providerId set, so the discriminator is "providerId is set AND no
    // userId is attached yet".
    const isDirectlyAttachedProvider =
      hasProviderRole && !affiliate.userId && !!affiliate.providerId
    if (isDirectlyAttachedProvider) {
      return true
    }

    // Otherwise, this affiliate is a registered user — include only if they've accepted
    // a provider-role invite that has a linked providerId (backfilled at invite
    // acceptance, US#16678), OR if they already have a providerId (migrated/pending scenario).
    const isActiveAffiliateProviderOnPractice = (
      affiliate.practiceInvites ?? []
    ).some(
      (invite) =>
        invite.inviteRoleDisplayIds.includes(PracticeRoleEnum.PROVIDER) &&
        (invite.inviteStatusDisplayId ===
          PracticeServiceInviteStatusDisplayId.ACCEPTED ||
          (!!affiliate.providerId &&
            invite.inviteStatusDisplayId !==
              PracticeServiceInviteStatusDisplayId.DECLINED &&
            invite.inviteStatusDisplayId !==
              PracticeServiceInviteStatusDisplayId.EXPIRED)),
    )
    return isActiveAffiliateProviderOnPractice
  })

  const providerIds = providerAffiliates
    .filter((affiliate) => !affiliate.userId)
    .map((affiliate) => affiliate.providerId)
    .filter((id): id is string => !!id)

  const providerUserIds = providerAffiliates
    .map((affiliate) => affiliate.userId)
    .filter((id): id is string => !!id)

  const providers = await fetchPublicProvidersData(providerIds, providerUserIds)

  const providersToReturn = providers.filter(Boolean) as Provider[]
  setDataStatusCallBack?.(DataStatus.LOADED)
  return providersToReturn
}

const fetchPublicProvidersData = async (
  providerIds: string[],
  providerUserIds: string[],
) => {
  try {
    const [providersById, providersByUserId] = await Promise.all([
      ...(!isEmpty(providerIds)
        ? [apiService.provider.get.PUBLIC_PROVIDERS_BY_ID(providerIds)]
        : []),
      ...(!isEmpty(providerUserIds)
        ? [
            apiService.provider.post.PROVIDERS_FROM_USER_IDS({
              userIds: providerUserIds,
            }),
          ]
        : []),
    ])

    return [...(providersById ?? []), ...(providersByUserId ?? [])].filter(
      Boolean,
    )
  } catch (error) {
    appInsightsLogger.traceError(error)
    throw error
  }
}

export const fetchPracticeProfileData = async (
  practiceId: string,
  isHaUser = false,
  setDataStatusCallBack?: (dataStatus: DataStatus) => void,
): Promise<PublicProfilePractice | undefined> => {
  try {
    setDataStatusCallBack?.(DataStatus.LOADING)

    const [practiceData, practiceTagsData, practicePublicAffiliates] =
      await Promise.all([
        apiService.practice.get.PUBLIC_PRACTICE_BY_ID({ practiceId }),
        isHaUser
          ? apiService.practice.get.PRACTICE_TAGS_BY_ID({ practiceId })
          : Promise.resolve([]),
        apiService.practice.get.PRACTICE_PUBLIC_AFFILIATES_BY_ID({
          practiceId,
          onlyProviders: "true",
        }),
      ])

    const providersInfo = (
      await fetchPracticeAffiliatesData(practicePublicAffiliates ?? [])
    ).filter(
      (provider) =>
        provider.providerStatusDisplayId === ProviderStatusDisplayIdEnum.ACTIVE,
    )

    practiceData.locations.forEach((location) => {
      location.stateOrTerritory = location.stateOrTerritory || ""
    })

    const practiceProfileData: PublicProfilePractice = {
      ...practiceData,
      tags: practiceTagsData,
      providersInfo: providersInfo,
      typeId: practiceData.typeId || PracticeTypeDisplayId.STANDARD,
      authReleaseRequired: practiceData.authReleaseRequired,
      liabilityInsurances: practiceData.liabilityInsurances || [],
    }

    setDataStatusCallBack?.(DataStatus.LOADED)

    // TODO: add more try/catch blocks with any more hydration calls that return expected 404s here
    // because 404s cannot gracefully return along with the other promise.all calls above

    return practiceProfileData
  } catch (error) {
    console.error("Error fetching practice data:", error)
    setDataStatusCallBack?.(DataStatus.ERROR)
  }

  return undefined
}
